import { ActionIcon, Alert, Badge, Divider, Flex, Group, Stack, Text, Tooltip } from '@mantine/core';
import StepCardTitle from '@/components/StepCard/StepCardTitle.tsx';
import React, { useCallback, useMemo, useState } from 'react';
import {
  AutomationStep,
  AutomationStepWithChildren,
  AutomationStepWithDirectChildren,
  FailureHandlerStep,
  LocalSubflowCallStep,
} from '@/types/automationSteps.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { HiArrowLeft, HiOutlineSquare2Stack, HiOutlineTrash } from 'react-icons/hi2';
import { useActiveStep } from '@/layouts/AutomationPageWrapper/hooks/useActiveStep.ts';
import { usePinnedStep } from '@/hooks/usePinnedStep.ts';
import { ConditionAction, StepType } from '@/types/automationFields';
import { TriggerForm } from '@/pages/Editor/components/StepSidebar/StepForms/TriggerStep/TriggerForm.tsx';
import { DispatchForm } from '@/pages/Editor/components/StepSidebar/StepForms/DispatchForm.tsx';
import { ExtractDataForm } from './StepForms/ExtractDataStepForm/ExtractDataForm';
import { IterateForm } from './StepForms/IterateStep/IterateForm';
import { RepeatForm } from '@/pages/Editor/components/StepSidebar/StepForms/RepeatStep/RepeatForm.tsx';
import { CreateDirectoryForm } from '@/pages/Editor/components/StepSidebar/StepForms/CreateDirectoryForm.tsx';
import { GetFileFromDirectoryForm } from '@/pages/Editor/components/StepSidebar/StepForms/GetFileFromDirectoryStep/GetFileFromDirectoryForm.tsx';
import { FilterForm } from '@/pages/Editor/components/StepSidebar/StepForms/FilterStep/FilterForm.tsx';
import { TransformDataForm } from '@/pages/Editor/components/StepSidebar/StepForms/TransformDataStep/TransformDataForm.tsx';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import ConfirmationModal from '@/components/ConfirmationModal';
import DeleteImpactWarning from '@/components/DeleteImpactWarning.tsx';
import useDeleteStep from '@/hooks/api/steps/useDeleteStep';
import useDuplicateStep from '@/hooks/api/steps/useDuplicateStep';
import { useStepContext } from '@/context/StepContext/useStepContext.ts';
import { useClickOutside, useHover } from '@mantine/hooks';
import { EditableText } from '@/components/EditableText.tsx';
import ChildStepsSection from '@/pages/Editor/components/StepSidebar/ChildStepsSection.tsx';
import { useEditorState } from '@/state/editorState.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import { TiPin, TiPinOutline } from 'react-icons/ti';
import { ConditionalForm } from '@/pages/Editor/components/StepSidebar/StepForms/ConditionalStep/ConditionalForm.tsx';
import { getStepDisabledInfo, isStepWithChildren } from '@/utils/stepUtils.ts';
import { STEP_DESCRIPTION_MAX_LENGTH } from '@/utils/constants.ts';
import { ValidateStepForm } from '@/pages/Editor/components/StepSidebar/StepForms/ValidateStep/ValidateStepForm.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { collectDeleteImpacts } from '@/utils/deleteImpacts.ts';
import { RunMetadataForm } from '@/pages/Editor/components/StepSidebar/StepForms/RunMetadataForm.tsx';
import { RetryForm } from '@/pages/Editor/components/StepSidebar/StepForms/RetryStep/RetryForm.tsx';
import { FailureHandlerForm } from '@/pages/Editor/components/StepSidebar/StepForms/FailureHandlerStep/FailureHandlerForm.tsx';
import SchemaStepIssues from '@/pages/Editor/components/StepSidebar/SchemaStepIssues.tsx';
import StepConnectionsPanel from '@/pages/Editor/components/StepSidebar/StepConnectionsPanel.tsx';
import { SubAutomationForm } from '@/pages/Editor/components/StepSidebar/StepForms/SubAutomationStep/SubAutomationForm.tsx';
import { CapabilityHelpProvider } from '@/pages/Editor/components/StepSidebar/CapabilityHelpContext.tsx';
import StepReferencePanel from '@/pages/Editor/components/StepSidebar/StepReferencePanel.tsx';
import { conditionalBranchLabel } from '@/pages/Editor/components/StepSidebar/conditionalBranchLabel.ts';
import { HumanDecisionGateForm } from '@/pages/Editor/components/StepSidebar/StepForms/HumanDecisionGateForm.tsx';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import { EditorCompositionCapabilities } from '@/hooks/api/automations/useRetrieveEditorCompositionCapabilities.ts';
import { isForgeManagedModuleStructure } from '@/pages/Editor/components/LocalSubflows/localSubflowEditorUtils.ts';

type props = {
  step: AutomationStep;
  hasParentStep: boolean;
  moduleCapabilities?: EditorCompositionCapabilities;
  moduleCapabilitiesLoading?: boolean;
  scopedSteps?: AutomationStep[];
};

const LocalSubflowCallForm = ({ step }: { step: LocalSubflowCallStep }) => {
  const [removeOpened, setRemoveOpened] = useState(false);
  const { mutate: applyOperations, isPending } = useCompositionOperations();
  const { setActiveCompositionTarget } = useActiveCompositionTarget();
  const { data: version } = useRetrieveLatestAutomationVersion();
  const subflow = version?.local_subflows?.find((candidate) => candidate.subflow_id === step.subflow_id);
  return (
    <>
      <Alert color="gray" title="Version-local subflow call">
        <Stack gap="xs">
          <Text size="sm">This call uses a private, typed module in the parent automation version.</Text>
          <Text size="xs" c="dimmed">
            Inputs: {step.input_bindings.length} · outputs: {step.output_bindings.length}
          </Text>
          {subflow?.declared_effect_boundary === 'declared' && (
            <Badge color="orange" variant="light" w="fit-content">
              This call can execute declared external effects
            </Badge>
          )}
          <StyledButton
            size="sm"
            variant="outline"
            onClick={() => void setActiveCompositionTarget({ scope_kind: 'local_subflow', subflow_id: step.subflow_id })}
          >
            Open local module
          </StyledButton>
          <StyledButton size="sm" variant="outline" color="red" onClick={() => setRemoveOpened(true)}>
            Remove this call
          </StyledButton>
        </Stack>
      </Alert>
      <ConfirmationModal
        opened={removeOpened}
        onClose={() => setRemoveOpened(false)}
        title="Remove local module call?"
        message="This removes only this root call. The private module remains available to its other callers."
        confirmText="Remove call"
        confirmType="delete"
        confirmLoading={isPending}
        onConfirm={() => applyOperations([{ op_type: 'delete_local_subflow_call', step_id: step.step_id }])}
      />
    </>
  );
};

const STEP_RENDERERS = {
  [StepType.Trigger]: TriggerForm,
  [StepType.Dispatch]: DispatchForm,
  [StepType.ExtractData]: ExtractDataForm,
  [StepType.TransformData]: TransformDataForm,
  [StepType.SubAutomation]: SubAutomationForm,
  [StepType.RunMetadata]: RunMetadataForm,
  [StepType.Iterate]: IterateForm,
  [StepType.Repeat]: RepeatForm,
  [StepType.Retry]: RetryForm,
  [StepType.FailureHandler]: FailureHandlerForm,
  [StepType.Conditional]: ConditionalForm,
  [StepType.CreateDirectory]: CreateDirectoryForm,
  [StepType.GetFileFromDirectory]: GetFileFromDirectoryForm,
  [StepType.Filter]: FilterForm,
  [StepType.Validate]: ValidateStepForm,
  [StepType.HumanDecisionGate]: HumanDecisionGateForm,
  [StepType.LocalSubflowCall]: LocalSubflowCallForm,
};

type DescriptionField = {
  description: string;
  name: string;
};

const buttonBoxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(0, 0, 0, 0.04)';

const SidebarStepCard = ({
  step,
  hasParentStep,
  moduleCapabilities,
  moduleCapabilitiesLoading = false,
  scopedSteps,
}: props) => {
  const { setActiveStep } = useActiveStep();
  const { target } = useActiveCompositionTarget();
  const { isStepPinned, togglePinnedStep } = usePinnedStep(step, undefined);

  const StepTypeRenderer = useMemo(() => STEP_RENDERERS[step.step_type], [step.step_type]);

  const { fields, onChange } = useUpdateStepFields<DescriptionField>({ step });

  const { mutate: deleteStep } = useDeleteStep();
  const { mutate: duplicateStep } = useDuplicateStep();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();

  const { scrollToActiveStep } = useStepContext();
  const { hovered, ref } = useHover();

  const isTriggerStep = step.step_type === StepType.Trigger;
  const isLocalSubflowCall = step.step_type === StepType.LocalSubflowCall;
  const isModuleStep = target.scope_kind === 'local_subflow';
  const canEditModuleName = !isModuleStep || (
    moduleCapabilities?.supported_operations.includes('update_local_subflow_step_fields') &&
    moduleCapabilities.editable_fields.includes('name')
  );
  const canEditModuleDescription = !isModuleStep || (
    moduleCapabilities?.supported_operations.includes('update_local_subflow_step_fields') &&
    moduleCapabilities.editable_fields.includes('description')
  );
  const canDuplicateModuleStep = !isModuleStep || moduleCapabilities?.supported_operations.includes(
    'duplicate_local_subflow_step',
  );
  const canDeleteModuleStep = !isModuleStep || moduleCapabilities?.supported_operations.includes(
    'delete_local_subflow_step',
  );
  const canRenderModuleStepForm = !isModuleStep || Boolean(
    moduleCapabilities?.supported_operations.includes('update_local_subflow_step_fields')
    || moduleCapabilities?.supported_operations.includes('replace_data_input_group'),
  );
  const isForgeManagedStructure = isModuleStep && isForgeManagedModuleStructure(step.step_type);

  const setNewStepId = useEditorState((s) => s.setNewStepId);

  const isNewStep = useIsNewStep(step?.step_id ?? '');
  const compositionSteps = isModuleStep ? scopedSteps : latestVersion?.steps;
  const moduleReadOnlyMessage = moduleCapabilitiesLoading
    ? 'Loading scoped editing policy…'
    : moduleCapabilities?.read_only_reason ?? 'This module step is available for review only.';

  const deleteImpacts = useMemo(
    () => collectDeleteImpacts(compositionSteps, { type: 'step', stepId: step.step_id }),
    [compositionSteps, step.step_id],
  );
  const disabledInfo = useMemo(
    () => getStepDisabledInfo(compositionSteps, step.step_id),
    [compositionSteps, step.step_id],
  );

  const onOutsideClick = useCallback(() => {
    // if (isActiveStep) setActiveStep(undefined);
    if (isNewStep) setNewStepId(undefined);
  }, [isNewStep, setNewStepId]);

  const clickOutsideRef = useClickOutside(onOutsideClick);

  return (
    <Stack w={'100%'} gap={0} ref={clickOutsideRef}>
      <Group justify={'space-between'} wrap={'nowrap'} px={'sm'} py={'8px'}>
        <StyledButton
          size={'sm'}
          variant={'subtleIcon'}
          aria-label="Close step sidebar"
          onClick={() => setActiveStep(undefined)}
          w={'28px'}
          h={'28px'}
        >
          <HiArrowLeft size={15} color="#9E9E9E" />
        </StyledButton>

        {!isTriggerStep && !isLocalSubflowCall && (
          <Group gap={'xs'} wrap={'nowrap'}>
            {canDuplicateModuleStep && <Tooltip label={'Duplicate Step'} openDelay={250}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                style={{ boxShadow: buttonBoxShadow }}
                onClick={() => duplicateStep({ step_id: step.step_id })}
              >
                <HiOutlineSquare2Stack color={'black'} />
              </ActionIcon>
            </Tooltip>}
            {!isModuleStep && <Tooltip
              label={
                hasParentStep
                  ? 'Cannot pin a child step'
                  : isStepPinned
                    ? 'Un-pin step'
                    : 'Pin Step'
              }
              openDelay={250}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                style={{ boxShadow: buttonBoxShadow }}
                disabled={hasParentStep}
                onClick={togglePinnedStep}
              >
                {isStepPinned ? (
                  <TiPin color={'black'} />
                ) : (
                  <TiPinOutline color={'black'} style={{ strokeWidth: 0 }} />
                )}
              </ActionIcon>
            </Tooltip>}
            {canDeleteModuleStep && <Tooltip label={'Delete Step'} openDelay={250}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                style={{ boxShadow: buttonBoxShadow }}
                onClick={() => setShowDeleteModal(true)}
              >
                <HiOutlineTrash color={'#F34F52'} />
              </ActionIcon>
            </Tooltip>}
            {/*<StyledMenu>*/}
            {/*  <Menu.Target>*/}
            {/*    <ActionIcon variant="subtle" color="gray" size="sm">*/}
            {/*      <HiDotsVertical />*/}
            {/*    </ActionIcon>*/}
            {/*  </Menu.Target>*/}
            {/*  <Menu.Dropdown w="150px">*/}
            {/*    <Menu.Item onClick={() => duplicateStep({ step_id: step.step_id })}>*/}
            {/*      Duplicate*/}
            {/*    </Menu.Item>*/}
            {/*    {!hasParentStep && (*/}
            {/*      <Menu.Item onClick={togglePinnedStep}>*/}
            {/*        {isStepPinned ? 'Un-pin step' : 'Pin Step'}*/}
            {/*      </Menu.Item>*/}
            {/*    )}*/}

            {/*    <Menu.Item onClick={() => setShowDeleteModal(true)}>Delete</Menu.Item>*/}
            {/*  </Menu.Dropdown>*/}
            {/*</StyledMenu>*/}
          </Group>
        )}
      </Group>
      <Divider style={{ borderColor: '#EAEAEA' }} />
      <Stack px={'xs'} py={'6px'} gap={'2px'}>
        <Flex
          // mx={'-2px'}
          px={'xs'}
          py={'xs'}
          style={{ borderRadius: '5px', backgroundColor: hovered ? '#F7F7F7' : 'transparent' }}
          ref={ref}
          onClick={scrollToActiveStep}
        >
          <Group justify="space-between" align="center" wrap="nowrap" w="100%">
            <StepCardTitle step={step} showPin={true} />
            {disabledInfo.isEffectivelyDisabled && (
              <Tooltip
                label={
                  disabledInfo.isInheritedDisabled
                    ? 'Disabled by an upstream step'
                    : 'Disabled directly'
                }
                openDelay={250}
              >
                <Badge color="gray" variant="light" size="xs" style={{ flexShrink: 0 }}>
                  Disabled
                </Badge>
              </Tooltip>
            )}
          </Group>
        </Flex>

        {isModuleStep && (canEditModuleName || canEditModuleDescription) ? (
          <>
            {canEditModuleName && (
              <EditableText
                name={fields.name}
                onSave={(newValue) => onChange({ name: newValue })}
                saving={false}
                size="sm"
                placeholder="Name this step"
                maxLength={120}
                fw={600}
              />
            )}
            {canEditModuleDescription && (
              <EditableText
                name={fields.description}
                onSave={(newValue) => onChange({ description: newValue })}
                saving={false}
                size={'sm'}
                inputType={'textarea'}
                lineClamp={3}
                placeholder={'Add a description'}
                maxLength={STEP_DESCRIPTION_MAX_LENGTH}
                fw={400}
              />
            )}
          </>
        ) : isModuleStep ? (
          <Alert color="gray">{moduleReadOnlyMessage}</Alert>
        ) : isLocalSubflowCall ? null : (
          <EditableText
            name={fields.description}
            onSave={(newValue) => onChange({ description: newValue })}
            saving={false}
            size={'sm'}
            inputType={'textarea'}
            lineClamp={3}
            placeholder={'Add a description'}
            maxLength={STEP_DESCRIPTION_MAX_LENGTH}
            fw={400}
          />
        )}
      </Stack>
      <Divider style={{ borderColor: '#EAEAEA' }} />
      <Stack p={'md'}>
        {!canRenderModuleStepForm ? (
          <Alert color="gray" title="Scoped module step">
            {moduleReadOnlyMessage}
          </Alert>
        ) : isForgeManagedStructure ? (
          <Alert color="gray" title="Protected module structure">
            This structural step is available for review and child-step arrangement in the module canvas. Its
            decision, retry, and recovery configuration is protected from direct edits.
          </Alert>
        ) : (
          <>
            <SchemaStepIssues step={step} />
            <CapabilityHelpProvider stepType={step.step_type}>
              {/* Renderer selection is keyed by the step discriminant. */}
              {/* @ts-expect-error The lookup preserves this discriminated-union relationship at runtime. */}
              <StepTypeRenderer step={step} hasParentStep={hasParentStep} />
            </CapabilityHelpProvider>
          </>
        )}
        {!isModuleStep && (
          <StepConnectionsPanel
            steps={latestVersion?.steps}
            stepId={step.step_id}
            onSelectStep={setActiveStep}
          />
        )}
        <StepReferencePanel stepType={step.step_type} />
      </Stack>

      {isStepWithChildren(step) && (
        <>
          <Divider my={'6px'} />
          {[StepType.Iterate, StepType.Repeat, StepType.Retry].includes(step.step_type) && (
            <ChildStepsSection
              step={step as AutomationStepWithChildren}
              label={
                step.step_type === StepType.Iterate
                  ? `For each item (${(step as AutomationStepWithDirectChildren).children.length})`
                  : step.step_type === StepType.Retry
                    ? `Retry steps (${(step as AutomationStepWithDirectChildren).children.length})`
                    : `Repeated steps (${(step as AutomationStepWithDirectChildren).children.length})`
              }
              childrenSteps={(step as AutomationStepWithDirectChildren).children}
            />
          )}
          {step.step_type === StepType.FailureHandler && (
            <>
              <ChildStepsSection
                step={step as FailureHandlerStep}
                label={`Main work (${step.children.length})`}
                childrenSteps={step.children}
              />
              <ChildStepsSection
                step={step as FailureHandlerStep}
                label={`Recovery steps (${step.handler_children.length})`}
                childrenSteps={step.handler_children}
              />
            </>
          )}
          {step.step_type === StepType.Conditional && (
            <>
              {step.conditions.map((condition, index) => {
                if (condition.action !== ConditionAction.ChildSteps) {
                  return null;
                }
                return (
                  <ChildStepsSection
                    key={condition.condition_id}
                    step={step as AutomationStepWithChildren}
                    label={conditionalBranchLabel(condition, index)}
                    childrenSteps={condition.children}
                    childIndex={index}
                  />
                );
              })}
            </>
          )}
        </>
      )}

      <ConfirmationModal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Step"
        message={<DeleteImpactWarning impacts={deleteImpacts} itemLabel="step" />}
        onConfirm={() => deleteStep({ step_id: step.step_id })}
        confirmType="delete"
        confirmText="Delete"
      />
    </Stack>
  );
};

export default SidebarStepCard;
